import React, { useEffect, useCallback, useMemo } from 'react'
import { connect } from 'react-redux'
import PageToolbar from './PageToolbar.js'
import redux from '../../redux/index.js'
import SearchField from '../SearchField.js'
import DeleteItem from '../dialogs/DeleteItem.js'
import { TablePagination } from '@mui/material'
import {
  Table,
  Link,
  styled,
  Tag,
  TD as Td
} from '@pergas-common/pergas-components'
import {
  Add,
  Remove,
  OpenExternal
} from '@pergas-common/pergas-icons'
import { createDateAccessor } from './columns.js'
import EditTag from '../dialogs/EditTag.js'
import { TagIcon } from '../icons.js'

const TableHolder = styled.div`
  display: flex;
  position: absolute;
  top: 100px;
  bottom: 0;
  overflow-y: scroll;
`

const TagHolder = styled.span`
  margin-left: 5px;
  margin-right: 8px;
`

const Spacer = styled.div`
  display: inline-block;
  margin: 2px;
`

const ROWS_PER_PAGE = [
  25,
  50,
  100,
  500,
  1000
]

const TagPage = ({
  locale,
  rows,
  limit,
  offset,
  orderBy,
  order,
  search,
  resetSearch,
  getPageItems,
  setOrder,
  setLimit,
  setOffset,
  setSearch,
  canUpdate,
  canDelete,
  deleteItem,
  onDeleteItemClick,
  onDeleteOk,
  onDeleteCancel,
  editItem,
  onEditItemClick,
  onEditOk,
  onEditCancel,
  canCreate,
  onShowAddDialog
}) => {
  useEffect(getPageItems, [limit, offset, orderBy, order, search])

  const manualSort = useCallback(({ id, isSorted, isSortedDesc }) => {
    if (isSorted && !isSortedDesc) {
      setOrder(id, 'desc')
    } else if (isSorted && isSortedDesc) {
      setOrder('', '')
    } else {
      setOrder(id, 'asc')
    }
  }, [setOrder])

  const columns = useMemo(() => {
    return [
      {
        Header: locale.name,
        Cell: ({ cell }) => {
          const { row: { original } } = cell
          return (
            <Td
              {...cell.getCellProps()} left={
                <>
                  <TagIcon style={{ width: 16, height: 16 }} />
                  <TagHolder>
                    <Tag textColor='#FFFFFF' color={original.color}>{original.name}</Tag>
                  </TagHolder>
                </>
            }
            />
          )
        },
        canSort: true,
        id: 'name',
        size: 'sm',
        manualSort,
        sortType: () => {}
      },
      {
        Header: locale.description,
        manualSort,
        accessor: 'description',
        sortType: () => {}
      },
      {
        id: 'tag_type_name',
        Header: locale.tag_type,
        Cell: ({ cell }) => {
          const { row: { original } } = cell
          return (<Td {...cell.getCellProps()} left={locale[original.tag_type_name] || ''} />)
        },
        isSortable: false
      },
      {
        id: 'updated_at',
        Header: locale.updated_at,
        manualSort,
        accessor: createDateAccessor('updated_at'),
        sortType: () => {}
      },
      {
        id: 'toolbar',
        Header: locale.tool_belt,
        Cell: ({ cell }) => {
          const { row: { original } } = cell
          return (
            <Td
              {...cell.getCellProps()}
              left={
                <>
                  {canUpdate && <Spacer><Link onClickHandler={() => { onEditItemClick(original) }}><OpenExternal color='#28afe0' width={20} height={20} /></Link></Spacer>}
                </>
              }
              right={canDelete && !original.read_only && <Link onClickHandler={() => { onDeleteItemClick(original) }}><Remove width={20} height={20} color='#FF5656' /></Link>}
            />
          )
        },
        size: 'sm',
        isSortable: false
      }
    ]
  }, [locale, canUpdate, canDelete, manualSort, onDeleteItemClick, onEditItemClick])

  let initialSortBy = []
  if (orderBy) {
    initialSortBy = [{
      id: orderBy,
      desc: order === 'desc'
    }]
  }

  return (
    <>
      {editItem && <EditTag isEditing item={editItem} onOk={onEditOk} onCancel={onEditCancel} />}
      {deleteItem && <DeleteItem text={deleteItem.name} onOk={() => { onDeleteOk(deleteItem) }} onCancel={onDeleteCancel} />}
      <PageToolbar left={<><TagIcon style={{ width: 20, height: 20 }} /><span>{locale.tags}</span> {canCreate && <Link onClickHandler={onShowAddDialog}><Add width={20} height={20} color='#28afe0' /></Link>}</>}>
        <SearchField resetSearch={resetSearch} value={search} onChange={setSearch} />
      </PageToolbar>
      <TableHolder>
        <Table columns={columns} data={rows} initialPageSize={limit} initialSortBy={initialSortBy}>
          {({ setPageSize }) => (
            <TablePagination
              rowsPerPage={limit}
              rowsPerPageOptions={ROWS_PER_PAGE}
              count={-1}
              page={offset / limit}
              labelRowsPerPage={locale.rows_per_page}
              labelDisplayedRows={({ from, to }) => `${from}-${to}`}
              onRowsPerPageChange={(e) => {
                setLimit(e.target.value)
                setPageSize(e.target.value)
              }}
              onPageChange={(e, number) => setOffset(number * limit)}
            />
          )}
        </Table>
      </TableHolder>
    </>
  )
}

const mapStateToProps = (state) => {
  const { locale, tag } = state
  const permissions = state.login.permissions
  const {
    pageItems,
    limit,
    offset,
    orderBy,
    order,
    search
  } = tag
  return {
    locale: locale.strings,
    rows: pageItems,
    limit,
    offset,
    orderBy,
    order,
    search,
    canUpdate: permissions.tag.canUpdate,
    canDelete: permissions.tag.canDelete,
    canCreate: permissions.tag.canCreate,
    deleteItem: state.tag.deleteItem,
    editItem: state.tag.editItem
  }
}

const mapDispatchToProps = (dispatch) => {
  const { actions: { tag } } = redux
  return {
    onShowAddDialog: () => {
      dispatch(tag.showAddTagDialog())
    },

    onEditOk: (t) => {
      dispatch(tag.hideEditTagDialog())
      dispatch(tag.updateTag(t))
    },
    onEditItemClick: (t) => {
      dispatch(tag.showEditTagDialog(t))
    },
    onEditCancel: () => {
      dispatch(tag.hideEditTagDialog())
    },

    onDeleteOk: (t) => {
      dispatch(tag.hideDeleteTagDialog())
      dispatch(tag.deleteTag(t))
    },
    onDeleteItemClick: (t) => {
      dispatch(tag.showDeleteTagDialog(t))
    },
    onDeleteCancel: () => {
      dispatch(tag.hideDeleteTagDialog())
    },

    getPageItems: () => dispatch(tag.getPageItems()),
    setOrder: (orderBy, order) => dispatch(tag.setOrder(orderBy, order)),
    setLimit: (limit) => dispatch(tag.setLimit(limit)),
    setOffset: (offset) => dispatch(tag.setOffset(offset)),
    setSearch: (search) => dispatch(tag.setSearch(search)),
    resetSearch: () => dispatch(tag.resetSearch())
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(TagPage)
